import { getLocalStorage } from '@ipevo-rd/service-web-utils'
import { AuthErrorCodeEnum } from '../../enums/AuthErrorCodeEnum.js'
import { v1HttpClient } from '../index.js'
import type { ApiResponse } from './dto.js'

/**
 * 認證服務暫時無法使用時顯示的固定提示
 */
export const AUTH_UNAVAILABLE_MESSAGE = '認證服務暫時無法使用，請稍後再試'

/**
 * admin API 業務錯誤；保留後端回應碼供呼叫端決定對應行為。
 */
export class AdminApiError extends Error {
	/**
	 * 後端業務碼（0 以外才會拋出）
	 */
	code: number

	/**
	 * 建立帶有後端業務碼的 admin API 錯誤。
	 * @param code {number} 後端業務碼
	 * @param message {string} 後端人類可讀訊息
	 */
	constructor(code: number, message: string) {
		// 以後端訊息作為標準 Error 訊息
		super(message)
		// 保存業務碼供畫面區分錯誤情境
		this.code = code
		// 修正編譯後的原型鏈以維持 instanceof 判斷
		Object.setPrototypeOf(this, AdminApiError.prototype)
	}
}

/**
 * 定義 admin 請求遇到認證錯誤時的應用層處理函式。
 */
export interface AuthErrorHandlers {
	/**
	 * 後端回傳 token 無效、過期、已登出或驗證失敗（1000003 / 1000004）時呼叫
	 */
	onTokenInvalid?: () => void
	/**
	 * 後端回傳尚未完成 2FA 時呼叫
	 */
	onTwoFaRequired?: () => void
}

/**
 * 目前註冊的 admin 認證錯誤處理函式
 */
let authErrorHandlers: AuthErrorHandlers = {}

/**
 * 註冊 admin 認證錯誤處理函式
 *
 * @param handlers {AuthErrorHandlers} token 失效與需要 2FA 時的處理函式
 * @returns {void} 不回傳內容
 */
export const setAuthErrorHandlers = (handlers: AuthErrorHandlers): void => {
	// 以應用啟動時提供的函式取代目前設定
	authErrorHandlers = handlers
}

/**
 * 驗證 admin API 回應並執行共用的認證與業務錯誤處理。
 * @template T 回應 data 的型別
 * @param result {unknown} 尚未驗證的 admin API 回應
 * @param options {{ skipAuthErrorHandlers?: boolean, requestToken?: unknown }} skipAuthErrorHandlers 是否略過共用認證錯誤處理；requestToken 為送出請求當下的 token，與目前 token 不同時視為舊回應而略過認證處理
 * @returns {ApiResponse<T>} 已確認業務碼為成功的完整回應
 */
export const handleAdminResponse = <T>(
	result: unknown,
	options: { skipAuthErrorHandlers?: boolean; requestToken?: unknown } = {},
): ApiResponse<T> => {
	// 回應必須是物件且包含有限數字業務碼，避免把錯誤格式誤判為成功
	if (
		typeof result !== 'object' ||
		result === null ||
		!('code' in result) ||
		typeof result.code !== 'number' ||
		!Number.isFinite(result.code)
	) {
		// 以格式錯誤中止後續業務碼判斷
		throw new Error('admin API 回應格式錯誤：缺少有效的 code')
	}

	// 業務碼已完成執行期驗證，可安全轉為共用回應型別
	const response = result as ApiResponse<T>

	// 認證服務暫時無法使用時以固定訊息中止，不呼叫任何認證處理器
	if (response.code === AuthErrorCodeEnum.AuthUnavailable) {
		throw new AdminApiError(response.code, AUTH_UNAVAILABLE_MESSAGE)
	}

	// 回應屬於舊 token 時不應影響目前登入狀態，未提供 requestToken 則維持原行為
	const isCurrentToken =
		options.requestToken === undefined ||
		options.requestToken === getLocalStorage('token')

	// token 無效或驗證失敗（admin 路徑上 1000004 即 token 問題）時交由 Dashboard 清除登入狀態並導回登入頁
	if (
		!options.skipAuthErrorHandlers &&
		isCurrentToken &&
		(response.code === AuthErrorCodeEnum.Token ||
			response.code === AuthErrorCodeEnum.Forbidden)
	) {
		// 通知應用層執行 token 失效流程
		authErrorHandlers.onTokenInvalid?.()
	}

	// 尚未完成 2FA 時交由 Dashboard 導向驗證頁
	if (
		!options.skipAuthErrorHandlers &&
		isCurrentToken &&
		response.code === AuthErrorCodeEnum.TwoFaRequired
	) {
		// 通知應用層執行 2FA 驗證流程
		authErrorHandlers.onTwoFaRequired?.()
	}

	// 一般業務錯誤統一保留 code 後拋出
	if (response.code !== 0) {
		// 將後端業務碼與訊息保留給呼叫端判斷
		throw new AdminApiError(response.code, response.message)
	}

	// 將已驗證成功的完整回應交回呼叫端
	return response
}

/**
 * 執行 admin 傳輸並把逾時、無回應的網路錯誤與 5xx 閘道錯誤轉成認證暫時無法使用。
 * 後端一律以 200 回應，5xx 代表閘道或平台故障；4xx 與非傳輸錯誤維持原樣拋出。
 *
 * @template T 傳輸回傳的型別
 * @param send {() => Promise<T>} 實際送出請求的函式
 * @returns {Promise<T>} 傳輸成功時原樣回傳的結果
 */
export const withTransportErrors = async <T>(
	send: () => Promise<T>,
): Promise<T> => {
	try {
		// 交由呼叫端送出請求
		return await send()
	} catch (error) {
		// 業務錯誤與非物件錯誤不屬於傳輸故障
		if (
			error instanceof AdminApiError ||
			typeof error !== 'object' ||
			error === null
		) {
			throw error
		}

		// 取出 axios 錯誤可能帶有的欄位
		const { code, message, response } = error as {
			code?: unknown
			message?: unknown
			response?: { status?: unknown }
		}

		// 沒有 HTTP 回應且屬逾時或網路錯誤，或收到 5xx 時視為暫時故障
		const isTransientFailure = response
			? typeof response.status === 'number' && response.status >= 500
			: ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(
					String(code),
			  ) || /timeout|Network Error/i.test(String(message))

		// 暫時故障改以固定訊息的業務錯誤拋出，不洩漏原始錯誤文字
		if (isTransientFailure) {
			throw new AdminApiError(
				AuthErrorCodeEnum.AuthUnavailable,
				AUTH_UNAVAILABLE_MESSAGE,
			)
		}

		// 其他錯誤原樣交給呼叫端
		throw error
	}
}

/**
 * 透過既有 V1 client 發送 admin 請求，並只在此範圍處理 code
 *
 * @template T 回應 data 的型別
 * @param options {Parameters<typeof v1HttpClient.request>[0]} 原樣傳給 V1 client 的請求設定
 * @param responseOptions {{ skipAuthErrorHandlers?: boolean }} 是否略過共用認證錯誤處理
 * @returns {ReturnType<typeof v1HttpClient.request>} V1 client 解析後的回應內容
 */
const request = async <T>(
	options: Parameters<typeof v1HttpClient.request>[0],
	responseOptions: { skipAuthErrorHandlers?: boolean } = {},
): Promise<ApiResponse<T>> => {
	// 送出前記下當下的 token，回應回來時用來辨認是否屬於舊登入
	const requestToken = getLocalStorage('token')
	// 保留既有 V1 request interceptor、通用回應解析與錯誤處理
	const result = await withTransportErrors(() =>
		v1HttpClient.request(options),
	)

	// 共用回應驗證與認證錯誤處理，讓所有 admin 傳輸路徑行為一致
	return handleAdminResponse<T>(result, { ...responseOptions, requestToken })
}

/**
 * admin Http Client，共用既有 V1 傳輸流程並限制認證碼處理範圍
 */
export const adminHttpClient = { request }
